import React, { useState, useCallback, useRef, useEffect } from 'react';
import { usePotionUIControls } from '@/hooks/tgs2025/potionmaker/use-potion-ui-controls';
import type { PotionUIControls, ButtonTextState } from '@/hooks/tgs2025/potionmaker/use-potion-ui-controls';

// Import the SVG content as a string
import potionSvgContent from '@/assets/Potion2_UI.svg?raw';

export interface SVGElementState {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  opacity?: number;
  transform?: string;
  className?: string;
}

// Re-export types for convenience
export type { PotionUIControls, ButtonTextState };

interface PotionUIProps {
  className?: string;
  width?: number | string;
  height?: number | string;
  onElementClick?: (elementId: string, element: Element) => void;
  onElementHover?: (elementId: string, element: Element | null) => void;
  elementStates?: Record<string, SVGElementState>;
  buttonSizes?: Record<string, number>; // New prop for button sizes (1.0 = 100%, 0.5 = 50%, etc.)
  onControlsReady?: (controls: PotionUIControls) => void; // Callback to provide AI controls
  // NEW: Accept external controls to share state with AI system
  externalControls?: {
    activeButton: string | null;
    buttonTexts: ButtonTextState;
    handleButtonToggle: (buttonId: string, lastClickTimeRef: React.MutableRefObject<number>) => void;
    controls: PotionUIControls;
  };
}

// Move button element mappings outside component to prevent re-creation
// Dual mapping system: separate concerns for animations vs UI state management

// For CSS animations - the actual SVG elements that need to rotate (old element IDs)
const BUTTON_ANIMATION_MAPPINGS = {
  'Button1': ['element-1949', 'element-1990'],
  'Button2': ['element-2038', 'element-2079'], 
  'Button3': ['element-2127', 'element-2168']
} as const;

// For UI/AI state management - the button group containers (new button group IDs)
const BUTTON_ELEMENT_MAPPINGS = {
  'Button1': ['Button1-2'], // The actual child group within Button1
  'Button2': ['Button2-2'], // The actual child group within Button2  
  'Button3': ['Button3-2'] // The actual child group within Button3
} as const;

export const PotionUI: React.FC<PotionUIProps> = ({
  className = '',
  width,
  height,
  onElementClick,
  onElementHover,
  elementStates = {},
  buttonSizes = { Button3: 0.5 }, // Default Button3 to 50%
  onControlsReady,
  externalControls
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastActiveButtonRef = useRef<string | null>(null);
  const svgMountedRef = useRef<boolean>(false);
  const lastClickTimeRef = useRef<number>(0);
  const elementCacheRef = useRef<Map<string, SVGElement>>(new Map());

  // Use external controls if provided, otherwise create our own
  const internalControls = usePotionUIControls();
  const { 
    activeButton, 
    buttonTexts, 
    handleButtonToggle, 
    controls 
  } = externalControls || internalControls;

  // Debug component lifecycle
  useEffect(() => {
    console.log('PotionUI mounted at:', Date.now());
    return () => {
      console.log('PotionUI unmounting at:', Date.now());
    };
  }, []);

  // Mount SVG only once and never touch it again
  useEffect(() => {
    if (!containerRef.current || svgMountedRef.current) return;
    
    const svgContainer = containerRef.current.querySelector('.potion-ui-svg');
    if (!svgContainer) return;

    // Process SVG only once
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(potionSvgContent, 'image/svg+xml');
    const svgElement = svgDoc.documentElement;

    // Add viewBox if not present and set dimensions
    if (width) svgElement.setAttribute('width', width.toString());
    if (height) svgElement.setAttribute('height', height.toString());

    // Find all elements that can be interactive (with class or id)
    const interactiveElements = svgElement.querySelectorAll('[class], [id], polygon, circle, rect, path, g');
    
    interactiveElements.forEach((element, index) => {
      // Get element identifier, handling SVG class properly
      let elementId = element.id;
      if (!elementId && element instanceof SVGElement && element.className) {
        if (typeof element.className === 'string') {
          elementId = element.className;
        } else if (element.className.baseVal) {
          elementId = element.className.baseVal;
        }
      }
      if (!elementId) {
        elementId = `element-${index}`;
      }
      
      // Add data attributes for identification
      element.setAttribute('data-element-id', elementId);
      element.setAttribute('data-interactive', 'true');

      // Special handling for button elements - just mark children without changing pointer events
      if (elementId === 'Button1' || elementId === 'Button2' || elementId === 'Button3' || 
          elementId === 'button1' || elementId === 'button2' || elementId === 'button3') {
        
        // Add button parent reference to all child elements (for efficient lookup)
        const children = element.querySelectorAll('*');
        children.forEach(child => {
          child.setAttribute('data-button-parent', elementId);
        });
      }
    });

    // Insert SVG directly into container - NEVER change this again
    svgContainer.innerHTML = svgElement.outerHTML;
    svgMountedRef.current = true;
    console.log('SVG mounted permanently at:', Date.now());
    
    // Pre-build element cache for performance
    elementCacheRef.current.clear();
    const mountedSvg = svgContainer.querySelector('svg');
    if (mountedSvg) {
      // Cache all button elements (for state management)
      Object.keys(BUTTON_ELEMENT_MAPPINGS).forEach(buttonId => {
        const element = mountedSvg.querySelector(`[data-element-id="${buttonId}"]`) as SVGElement ||
                       mountedSvg.querySelector(`[id="${buttonId}"]`) as SVGElement ||
                       mountedSvg.querySelector(`[data-element-id="${buttonId.toLowerCase()}"]`) as SVGElement;
        if (element) {
          elementCacheRef.current.set(`button-${buttonId}`, element);
        }
      });
      
      // Cache all UI elements (for UI state management) 
      Object.values(BUTTON_ELEMENT_MAPPINGS).flat().forEach(elementId => {
        const element = mountedSvg.querySelector(`[id="${elementId}"]`) as SVGElement ||
                       mountedSvg.querySelector(`[data-element-id="${elementId}"]`) as SVGElement;
        if (element) {
          elementCacheRef.current.set(`element-${elementId}`, element);
          console.log('Cached UI element:', elementId);
        } else {
          console.warn('Could not find UI element:', elementId);
        }
      });

      // Cache all animation elements (for CSS animations)
      Object.values(BUTTON_ANIMATION_MAPPINGS).flat().forEach(elementId => {
        const element = mountedSvg.querySelector(`[id="${elementId}"]`) as SVGElement ||
                       mountedSvg.querySelector(`[data-element-id="${elementId}"]`) as SVGElement;
        if (element) {
          elementCacheRef.current.set(`element-${elementId}`, element);
          console.log('Cached animation element:', elementId);
        } else {
          console.warn('Could not find animation element:', elementId);
        }
      });
      console.log('Initial element cache built with', elementCacheRef.current.size, 'elements');
    }
  }, [width, height]);

  // Provide AI controls to parent component
  useEffect(() => {
    if (onControlsReady) {
      onControlsReady(controls);
    }
  }, [onControlsReady, controls]);

  // Apply animations to active button elements - OPTIMIZED
  useEffect(() => {
    console.log('PotionUI animation effect dependency changed - activeButton:', activeButton);
    
    if (!containerRef.current) return;
    
    // Prevent unnecessary re-renders if activeButton hasn't actually changed
    if (lastActiveButtonRef.current === activeButton) {
      console.log('Skipping animation - activeButton unchanged:', activeButton);
      return;
    }
    
    const previousActiveButton = lastActiveButtonRef.current;
    lastActiveButtonRef.current = activeButton;

    // Skip if cache is empty (SVG not mounted yet)
    if (elementCacheRef.current.size === 0) {
      console.log('Skipping animation - cache not ready');
      return;
    }

    console.log('Animation effect triggered, activeButton:', activeButton, 'previousActiveButton:', previousActiveButton, 'timestamp:', Date.now());

    // Apply reverse animation to previously active elements (both deactivation and switching)
    if (previousActiveButton && BUTTON_ANIMATION_MAPPINGS[previousActiveButton as keyof typeof BUTTON_ANIMATION_MAPPINGS]) {
      console.log('Applying reverse animation to previous button:', previousActiveButton);
      
      const elementsToReverse = BUTTON_ANIMATION_MAPPINGS[previousActiveButton as keyof typeof BUTTON_ANIMATION_MAPPINGS];
      
      elementsToReverse.forEach((elementId: string) => {
        const element = elementCacheRef.current.get(`element-${elementId}`);
        if (element) {
          // Calculate current rotation from animation
          const computedStyle = getComputedStyle(element);
          const matrix = computedStyle.transform;
          let currentRotation = 0;
          
          if (matrix !== 'none') {
            const values = matrix.split('(')[1].split(')')[0].split(',');
            const a = parseFloat(values[0]);
            const b = parseFloat(values[1]);
            currentRotation = Math.round(Math.atan2(b, a) * (180/Math.PI));
            if (currentRotation < 0) currentRotation += 360;
          }
          
          // Set CSS custom property for current rotation
          element.style.setProperty('--current-rotation', `${currentRotation}deg`);
          
          // Remove forward animation and add reverse animation
          element.classList.remove('button-animate');
          element.classList.add('button-animate-reverse');
          
          // Remove reverse class after animation completes
          setTimeout(() => {
            element.classList.remove('button-animate-reverse');
            element.style.removeProperty('--current-rotation');
          }, 2000); // Match the 2s animation duration
          
          console.log('Applied reverse animation to:', elementId, 'from rotation:', currentRotation);
        }
      });
      
      // Remove active class from the previous button with deactivation animation
      const previousButtonElement = elementCacheRef.current.get(`button-${previousActiveButton}`);
      if (previousButtonElement) {
        // Add deactivating class for animation
        previousButtonElement.classList.add('deactivating');
        previousButtonElement.classList.remove('active');
        
        // Remove deactivating class after animation completes
        setTimeout(() => {
          previousButtonElement.classList.remove('deactivating');
        }, 300); // Match the 0.3s animation duration
      }
    }

    // Apply forward animation to newly active button elements
    if (activeButton && BUTTON_ANIMATION_MAPPINGS[activeButton as keyof typeof BUTTON_ANIMATION_MAPPINGS]) {
      console.log('Applying forward animations for active button:', activeButton);
      
      // Add active class to the active button (use BUTTON_ELEMENT_MAPPINGS for button state)
      const activeButtonElement = elementCacheRef.current.get(`button-${activeButton}`);
      if (activeButtonElement) {
        activeButtonElement.classList.add('active');
        console.log('Added active class to button:', activeButton);
      }

      // Apply forward animation to associated elements (use BUTTON_ANIMATION_MAPPINGS for animations)
      const elementsToAnimate = BUTTON_ANIMATION_MAPPINGS[activeButton as keyof typeof BUTTON_ANIMATION_MAPPINGS];
      console.log('Elements to animate:', elementsToAnimate);
      
      elementsToAnimate.forEach((elementId: string) => {
        const element = elementCacheRef.current.get(`element-${elementId}`);
        if (element) {
          // Remove any existing reverse animation and apply forward animation
          element.classList.remove('button-animate-reverse');
          element.style.removeProperty('--current-rotation');
          element.classList.add('button-animate');
          console.log('Added button-animate class to:', elementId, 'Element found:', !!element);
        } else {
          console.warn('Could not find element to animate:', elementId, 'Cache size:', elementCacheRef.current.size);
          // Log all cached elements for debugging
          console.log('Cached elements:', Array.from(elementCacheRef.current.keys()));
        }
      });
    }

    // Clean up any remaining elements that aren't part of the active or previous button
    Object.keys(BUTTON_ANIMATION_MAPPINGS).forEach(buttonId => {
      if (buttonId !== activeButton && buttonId !== previousActiveButton) {
        // Clean button state (use BUTTON_ELEMENT_MAPPINGS for button state management)
        const buttonElement = elementCacheRef.current.get(`button-${buttonId}`);
        if (buttonElement) {
          buttonElement.classList.remove('active', 'deactivating');
        }
        
        // Clean animation elements (use BUTTON_ANIMATION_MAPPINGS for animations)
        const elementsToClean = BUTTON_ANIMATION_MAPPINGS[buttonId as keyof typeof BUTTON_ANIMATION_MAPPINGS];
        elementsToClean.forEach((elementId: string) => {
          const element = elementCacheRef.current.get(`element-${elementId}`);
          if (element) {
            element.classList.remove('button-animate', 'button-animate-reverse');
            element.style.removeProperty('--current-rotation');
          }
        });
      }
    });
  }, [activeButton]);

  // Handle width/height changes separately to avoid recreating the entire SVG
  useEffect(() => {
    if (!containerRef.current) return;
    
    const svgElement = containerRef.current.querySelector('svg');
    if (!svgElement) return;

    if (width) svgElement.setAttribute('width', width.toString());
    if (height) svgElement.setAttribute('height', height.toString());
  }, [width, height]);

  // Handle element interactions
  const handleElementClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as Element;
    
    // First, try efficient direct detection
    let elementId = target.getAttribute('data-element-id');
    let actualId = target.getAttribute('id');
    let buttonParent = target.getAttribute('data-button-parent');
    
    console.log('Click detected:', { elementId, actualId, buttonParent, tagName: target.tagName });
    
    // Determine the button ID efficiently first
    let buttonId = null;
    
    // Check direct button attributes
    if (elementId && (elementId.match(/^[Bb]utton[123]$/))) {
      buttonId = elementId;
    } else if (actualId && (actualId.match(/^[Bb]utton[123]$/))) {
      buttonId = actualId;
    } else if (buttonParent && (buttonParent.match(/^[Bb]utton[123]$/))) {
      buttonId = buttonParent;
    }
    
    // Fallback: quick parent check (max 2 levels)
    if (!buttonId) {
      let searchElement: Element | null = target.parentElement;
      for (let i = 0; i < 2 && searchElement; i++) {
        const parentId = searchElement.getAttribute('data-element-id') || searchElement.getAttribute('id');
        if (parentId && (parentId.match(/^[Bb]utton[123]$/))) {
          buttonId = parentId;
          break;
        }
        searchElement = searchElement.parentElement;
      }
    }
    
    if (buttonId) {
      // Normalize to uppercase for consistency
      const normalizedButtonId = buttonId.charAt(0).toUpperCase() + buttonId.slice(1).toLowerCase();
      console.log('Button clicked:', normalizedButtonId);
      handleButtonToggle(normalizedButtonId, lastClickTimeRef);
    } else {
      console.log('No button found');
    }
    
    // Call the original onElementClick if provided
    if (onElementClick && (elementId || actualId)) {
      onElementClick(elementId || actualId!, target);
    }
  }, [onElementClick, handleButtonToggle]);

  const handleElementMouseEnter = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as Element;
    const elementId = target.getAttribute('data-element-id');
    
    if (elementId && onElementHover) {
      onElementHover(elementId, target);
    }
  }, [onElementHover]);

  const handleElementMouseLeave = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as Element;
    const elementId = target.getAttribute('data-element-id');
    
    if (elementId && onElementHover) {
      onElementHover(elementId, null);
    }
  }, [onElementHover]);

  // Apply dynamic styles to elements
  useEffect(() => {
    if (!containerRef.current) return;

    const svgElement = containerRef.current.querySelector('svg');
    if (!svgElement) return;

    Object.entries(elementStates).forEach(([elementId, state]) => {
      const element = svgElement.querySelector(`[data-element-id="${elementId}"]`) as SVGElement;
      if (element) {
        if (state.fill !== undefined) element.style.fill = state.fill;
        if (state.stroke !== undefined) element.style.stroke = state.stroke;
        if (state.strokeWidth !== undefined) element.style.strokeWidth = state.strokeWidth.toString();
        if (state.opacity !== undefined) element.style.opacity = state.opacity.toString();
        if (state.transform !== undefined) {
          element.style.transform = state.transform;
          element.style.transformOrigin = 'center';
          (element.style as any).transformBox = 'fill-box';
        }
        if (state.className !== undefined && element.className) {
          if (typeof element.className === 'object' && element.className.baseVal !== undefined) {
            element.className.baseVal = state.className;
          } else {
            element.setAttribute('class', state.className);
          }
        }
      }
    });
  }, [elementStates]);

  return (
    <div 
      className={`potion-ui-container ${className}`} 
      ref={containerRef}
      style={{ 
        width: typeof width === 'string' ? width : width ? `${width}px` : '100%',
        height: typeof height === 'string' ? height : height ? `${height}px` : '100%'
      }}
    >
      <div
        onClick={handleElementClick}
        onMouseEnter={handleElementMouseEnter}
        onMouseLeave={handleElementMouseLeave}
        className="potion-ui-svg"
        style={{ 
          cursor: onElementClick ? 'pointer' : 'default',
          width: '100%',
          height: '100%'
        }}
      />

      {/* Button Text Overlays */}
      <div className={`button-text-overlay button1-text ${activeButton === 'Button1' ? 'active' : ''}`}>
        <div className="button-text-content">
          {buttonTexts.button1Text}
        </div>
      </div>

      <div className={`button-text-overlay button2-text ${activeButton === 'Button2' ? 'active' : ''}`}>
        <div className="button-text-content">
          {buttonTexts.button2Text}
        </div>
      </div>

      <div className={`button-text-overlay button3-text ${activeButton === 'Button3' ? 'active' : ''}`}>
        <div className="button-text-content">
          {buttonTexts.button3Text}
        </div>
      </div>
      
      <style>{`
        .potion-ui-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .potion-ui-svg {
          display: flex;
          align-items: center;
          justify-content: center;
      
        }
        
        .potion-ui-svg svg {
          max-width: 160%;
          max-height: 160%;
          width: auto;
          height: auto;
          top: -380px;
          position: relative;
        }

        /* Move button elements with CSS - now with configurable scaling */
        .potion-ui-svg [data-element-id="Button1"] {
          transform: translate(0px, 250px) scale(${buttonSizes.Button1 || 1});
          transform-origin: center;
          transform-box: view-box;
          transition: transform 0.3s ease-in-out;
        }
        
        .potion-ui-svg [data-element-id="Button2"] {
          transform: translate(0px, 250px) scale(${buttonSizes.Button2 || 1});
          transform-origin: center;
          transform-box: view-box;
          transition: transform 0.3s ease-in-out;
        }
        
        .potion-ui-svg [data-element-id="Button3"] {
          transform: translate(0px, 700px) scale(${buttonSizes.Button3 || 0.5});
          transform-origin: center;
          transform-box: view-box;
          transition: transform 0.3s ease-in-out;
        }

        .potion-ui-svg [data-element-id="textbox"] {
          transform: translate(0px, -50px);
          animation: rotateClockwiseTextbox 240s linear infinite;
          transform-box: fill-box;
          transform-origin: 50% 50%;
        }

        /* Button Text Overlay Styles */
        .button-text-overlay {
          position: absolute;
          pointer-events: none;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Arial', sans-serif;
          font-weight: bold;
          color: white;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
          z-index: 10;
          transition: all 0.3s ease-in-out;
        
        }

        .button-text-content {
          font-size: 1rem;
          text-align: center;
          white-space: nowrap;
          color: white;
          text-transform: uppercase;
          font-family: 'Yuji Syuku', serif;
        }

        /* Button 1 Text Positioning (Right button) */
        .button1-text {
          top: calc(75% + 270px);
          right: calc(50% - 370px);
          width: 120px;
          height: 40px;
          border: 1px solid red;
        }

        /* Button 2 Text Positioning (Left button) */
        .button2-text {
          top: calc(75% + 270px);
          left: calc(50% - 390px);
          width: 120px;
          height: 40px;
          border: 1px solid red;
        }

        /* Button 3 Text Positioning (Middle button) */
        .button3-text {
          top: calc(75% + 190px);
          left: 50%;
          transform: translateX(-50%);
          width: 120px;
          height: 40px;
          border: 1px solid red;
        }

        /* Scale text overlays with button animations */
        .button1-text.active {
          transform: translate(-320px, -510px) scale(${(buttonSizes.Button1 || 1) * 2.1}) !important;
        }

        .button1-text.active .button-text-content {
          font-size: 1.2rem;
        }

        .button2-text.active {
          transform: translate(290px, -510px) scale(${(buttonSizes.Button2 || 1) * 2.1}) !important;
        }

        .button2-text.active .button-text-content {
          font-size: 1.2rem;
        }

        .button3-text.active {
          transform: translateX(-50%) translateY(-420px) scale(2.1) !important;
        }

        .button3-text.active .button-text-content {
          font-size: 1.2rem;
        }

        @keyframes rotateClockwise {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes rotateClockwiseReverse {
          from {
            transform: rotate(var(--current-rotation, 0deg));
          }
          to {
            transform: rotate(0deg);
          }
        }

        @keyframes rotateClockwiseTextbox {
          from {
            transform: translate(0px, -50px) rotate(0deg);
          }
          to {
            transform: translate(0px, -50px) rotate(360deg);
          }
        }

        /* Button activation animations */
        @keyframes button1Activate {
          from {
            transform: translate(0px, 250px) scale(${buttonSizes.Button1 || 1});
          }
          to {
            transform: translate(-550px, -1100px) scale(${(buttonSizes.Button1 || 1) * 2.1});
          }
        }

        @keyframes button1Deactivate {
          from {
            transform: translate(-550px, -1100px) scale(${(buttonSizes.Button1 || 1) * 2.1});
          }
          to {
            transform: translate(0px, 250px) scale(${buttonSizes.Button1 || 1});
          }
        }

        @keyframes button2Activate {
          from {
            transform: translate(0px, 250px) scale(${buttonSizes.Button2 || 1});
          }
          to {
            transform: translate(550px, -1100px) scale(${(buttonSizes.Button2 || 1) * 2.1});
          }
        }

        @keyframes button2Deactivate {
          from {
            transform: translate(550px, -1100px) scale(${(buttonSizes.Button2 || 1) * 2.1});
          }
          to {
            transform: translate(0px, 250px) scale(${buttonSizes.Button2 || 1});
          }
        }

        @keyframes button3Activate {
          from {
            transform: translate(0px, 700px) scale(${buttonSizes.Button3 || 0.5});
          }
          to {
            transform: translate(0px, 0px) scale(${(buttonSizes.Button3 || 0.5) * 2.1});
          }
        }

        @keyframes button3Deactivate {
          from {
            transform: translate(0px, 0px) scale(${(buttonSizes.Button3 || 0.5) * 2.1});
          }
          to {
            transform: translate(0px, 700px) scale(${buttonSizes.Button3 || 0.5});
          }
        }

        /* Active button styling - handle both id and data-element-id */
        .potion-ui-svg [data-element-id="Button1"].active,
        .potion-ui-svg [data-element-id="button1"].active,
        .potion-ui-svg [id="Button1"].active {
          filter: brightness(1.2) drop-shadow(0 0 10px rgba(255, 255, 255, 0.5));
          animation: button1Activate 0.3s ease-in-out forwards;
        }

        .potion-ui-svg [data-element-id="Button2"].active,
        .potion-ui-svg [data-element-id="button2"].active,
        .potion-ui-svg [id="Button2"].active {
          filter: brightness(1.2) drop-shadow(0 0 10px rgba(255, 255, 255, 0.5));
          animation: button2Activate 0.3s ease-in-out forwards;
        }

        .potion-ui-svg [data-element-id="Button3"].active,
        .potion-ui-svg [data-element-id="button3"].active,
        .potion-ui-svg [id="Button3"].active {
          filter: brightness(1.2) drop-shadow(0 0 10px rgba(255, 255, 255, 0.5));
          animation: button3Activate 0.3s ease-in-out forwards;
        }

        /* Deactivation animations */
        .potion-ui-svg [data-element-id="Button1"].deactivating,
        .potion-ui-svg [data-element-id="button1"].deactivating,
        .potion-ui-svg [id="Button1"].deactivating {
          animation: button1Deactivate 0.3s ease-in-out forwards;
        }

        .potion-ui-svg [data-element-id="Button2"].deactivating,
        .potion-ui-svg [data-element-id="button2"].deactivating,
        .potion-ui-svg [id="Button2"].deactivating {
          animation: button2Deactivate 0.3s ease-in-out forwards;
        }

        .potion-ui-svg [data-element-id="Button3"].deactivating,
        .potion-ui-svg [data-element-id="button3"].deactivating,
        .potion-ui-svg [id="Button3"].deactivating {
          animation: button3Deactivate 0.3s ease-in-out forwards;
        }

        /* Button-activated animations - use CSS classes to avoid conflicts - Updated to use data-element-id */
        .potion-ui-svg [data-element-id="element-1949"].button-animate,
        .potion-ui-svg [data-element-id="element-1990"].button-animate,
        .potion-ui-svg [data-element-id="element-2038"].button-animate,
        .potion-ui-svg [data-element-id="element-2079"].button-animate,
        .potion-ui-svg [data-element-id="element-2127"].button-animate,
        .potion-ui-svg [data-element-id="element-2168"].button-animate {
          animation: rotateClockwise 40s linear infinite;
          transform-origin: 50% 50%;
          transform-box: fill-box;
        }

        /* Smooth reverse animation when deactivating - Updated to use data-element-id */
        .potion-ui-svg [data-element-id="element-1949"].button-animate-reverse,
        .potion-ui-svg [data-element-id="element-1990"].button-animate-reverse,
        .potion-ui-svg [data-element-id="element-2038"].button-animate-reverse,
        .potion-ui-svg [data-element-id="element-2079"].button-animate-reverse,
        .potion-ui-svg [data-element-id="element-2127"].button-animate-reverse,
        .potion-ui-svg [data-element-id="element-2168"].button-animate-reverse {
          animation: rotateClockwiseReverse 2s ease-in-out forwards;
          transform-origin: center center;
          transform-box: fill-box;
        } 
      `}</style>
    </div>
  );
};

// Custom hook for managing potion UI state
export const usePotionUI = () => {
  const [elementStates, setElementStates] = useState<Record<string, SVGElementState>>({});

  const updateElement = useCallback((elementId: string, newState: Partial<SVGElementState>) => {
    setElementStates(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        ...newState
      }
    }));
  }, []);

  const getElement = useCallback((elementId: string): SVGElementState => {
    return elementStates[elementId] || {};
  }, [elementStates]);

  const resetElement = useCallback((elementId: string) => {
    setElementStates(prev => {
      const newState = { ...prev };
      delete newState[elementId];
      return newState;
    });
  }, []);

  const resetAllElements = useCallback(() => {
    setElementStates({});
  }, []);

  return {
    elementStates,
    updateElement,
    getElement,
    resetElement,
    resetAllElements
  };
};

export default PotionUI;
